import * as React from 'react';
import toast from 'react-hot-toast';
import useScreenRecorder from '../../../hooks/useScreenRecorder';

import { apiPaths } from '../../../service/apiPaths';
import ApiService from '../../../service/ApiService';
import Modal from '../../../components/Model';

interface ScreenRecorderProps {
  onSave: (videoUrl: string) => void;
  category?: string;
  subcategory?: string;
}

const ScreenRecorder: React.FC<ScreenRecorderProps> = ({ onSave, category, subcategory }) => {
  const {
    blobUrl,
    pauseRecording,
    resetRecording,
    resumeRecording,
    startRecording,
    status,
    stopRecording,
  } = useScreenRecorder({
    audio: true,
    options: {
      mimeType: "video/webm;codecs=vp9",
    },
  });

  const [isDescModalOpen, setIsDescModalOpen] = React.useState<boolean>(false);
  const [tempDescription, setTempDescription] = React.useState<string>("");
  const [description, setDescription] = React.useState<string>("");

  const handleStartRecording = async () => {
    // Only require subcategory if category is provided (indicating category/subcategory context)
    if (category && !subcategory) {
      toast.error("Please select a subcategory before starting the recording");
      return;
    }
    setIsDescModalOpen(true);
  };

  const confirmStartRecording = async () => {
    try {
      setDescription(tempDescription.trim());
      setIsDescModalOpen(false);
      await startRecording();
      toast.success("Recording started");
    } catch (error) {
      console.error("Failed to start recording:", error);
      toast.error("Failed to start recording. Please try again.");
    }
  };

  const handleSave = async () => {
    if (!blobUrl) {
      toast.error("No recording to save");
      return;
    }

    try {
      // Convert blob URL to blob
      const response = await fetch(blobUrl);
      const blob = await response.blob();

      // Create form data
      const formData = new FormData();
      formData.append("video", blob, "recording.webm");
      if (category) formData.append("category", category);
      if (subcategory) formData.append("subcategory", subcategory);
      const currentUserId = localStorage.getItem("userId");
      if (currentUserId) formData.append("userId", currentUserId);
      if (description) formData.append("description", description);

      // Upload video
      const result = await ApiService({
        method: "POST",
        endpoint: apiPaths.upload_video,
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (result.videoUrl) {
        onSave(result.videoUrl);
        toast.success("Video saved successfully");
      }
    } catch (error) {
      console.error("Error saving video:", error);
      toast.error("Failed to save video");
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-[70vh] bg-[#fafafa]">
      {/* Status and Blob URL */}
      <div className="flex gap-4 mb-4 w-full max-w-4xl">
        <div className="flex-1 bg-gray-200 rounded-md p-2 flex flex-col items-center">
          <span className="text-xs uppercase text-gray-500 font-semibold">
            Status
          </span>
          <span className="text-lg font-medium">{status}</span>
        </div>
        <div className="flex-1 bg-gray-200 rounded-md p-2 flex flex-col items-center">
          <span className="text-xs uppercase text-gray-500 font-semibold">
            Blob URL
          </span>
          <span className="text-lg font-medium">
            {blobUrl ? "Ready" : "Waiting..."}
          </span>
        </div>
      </div>

      {/* Video */}
      <div className="relative w-[900px] h-[350px] rounded-2xl shadow-xl bg-black flex items-center justify-center mb-8">
        {blobUrl ? (
          <>
            <video
              id="sr-live-preview"
              src={blobUrl}
              controls
              playsInline
              className="w-full h-full object-cover rounded-2xl"
            />
            <button
              type="button"
              onClick={() => {
                const el = document.getElementById('sr-live-preview') as HTMLVideoElement | null;
                if (!el) return;
                if (el.requestFullscreen) {
                  el.requestFullscreen();
                // @ts-ignore
                } else if (el.webkitEnterFullscreen) {
                  // @ts-ignore
                  el.webkitEnterFullscreen();
                }
              }}
              className="absolute bottom-3 right-3 bg-black/60 text-white text-sm px-3 py-1.5 rounded hover:bg-black/80"
              title="Fullscreen"
            >
              Fullscreen
            </button>
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/4/4e/Pie_chart_example.svg"
              alt="Placeholder"
              className="w-full h-full object-cover rounded-2xl opacity-80"
            />
            <span className="absolute text-white text-3xl font-semibold drop-shadow-lg">
              Recording will appear here.
            </span>
          </div>
        )}
      </div>

      {/* Button */}
      <div className="flex gap-6 mt-4">
        {(status === "idle" || status === "permission-requested") && (
          <button
            onClick={handleStartRecording}
            disabled={category ? !subcategory : false}
            className="px-8 py-2 rounded-md bg-red-100 text-red-500 font-medium text-lg shadow-sm hover:bg-red-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Start recording
          </button>
        )}
        {status === "recording" && (
          <>
            <button
              onClick={stopRecording}
              className="px-8 py-2 rounded-md bg-red-100 text-red-500 font-medium text-lg shadow-sm hover:bg-red-200 transition"
            >
              Stop recording
            </button>
            <button
              onClick={pauseRecording}
              className="px-8 py-2 rounded-md bg-red-100 text-red-500 font-medium text-lg shadow-sm hover:bg-red-200 transition"
            >
              Pause recording
            </button>
          </>
        )}
        {status === "paused" && (
          <>
            <button
              onClick={resumeRecording}
              className="px-8 py-2 rounded-md bg-red-100 text-red-500 font-medium text-lg shadow-sm hover:bg-red-200 transition"
            >
              Resume recording
            </button>
            <button
              onClick={stopRecording}
              className="px-8 py-2 rounded-md bg-red-100 text-red-500 font-medium text-lg shadow-sm hover:bg-red-200 transition"
            >
              Stop recording
            </button>
          </>
        )}
      </div>
      {status === "stopped" && (
        <div className="flex gap-4">
          <button
            onClick={resetRecording}
            className="w-[300px] py-2 rounded-md bg-red-100 text-red-500 font-medium text-lg shadow-sm hover:bg-red-200 transition mt-8"
          >
            Reset recording
          </button>
          <button
            onClick={handleSave}
            className="w-[300px] py-2 rounded-md bg-green-100 text-green-500 font-medium text-lg shadow-sm hover:bg-green-200 transition mt-8"
          >
            Save recording
          </button>
        </div>
      )}

      <Modal isOpen={isDescModalOpen} onClose={() => setIsDescModalOpen(false)} style="w-[550px] max-w-[95vw]">
        <div className="w-[480px] max-w-[88vw]">
          <h4 className="text-lg font-semibold mb-3">Add description</h4>
          <p className="text-sm text-gray-600 mb-3">Provide a short description for this recording.</p>
          <textarea
            className="w-full border rounded px-3 py-2 min-h-[100px]"
            placeholder="Enter description (optional)"
            value={tempDescription}
            onChange={(e) => setTempDescription(e.target.value)}
          />
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setIsDescModalOpen(false)}
              className="px-4 py-2 rounded border hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmStartRecording}
              className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
            >
              Start recording
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ScreenRecorder;
